/* frontend/src/pages/NegotiationModal.js */
import React, { useEffect, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import api from '../api';
import { Trash2, Send } from 'lucide-react';
import {
  DialogHeader,
  DialogTitle,
  DialogFooter
} from '../components/ui/dialog';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Separator } from '../components/ui/separator';
import { ScrollArea } from '../components/ui/scroll-area';
import { Card, CardContent } from '../components/ui/card';

/* ---------- Componente de seleção com busca ---------- */
import { DirectCombobox } from '../components/ui/direct-combobox';

const SearchSelect = ({
  label, 
  options = [], 
  getOptionLabel, 
  onChange, 
  multi = false,
  value = multi ? [] : null,
  onSearch = null, // função assíncrona para buscar opções
  searchPlaceholder = null
}) => {  
  const { t } = useTranslation();  
  const [isSearching, setIsSearching] = React.useState(false);
  const [searchResults, setSearchResults] = React.useState([]);
  const [searchQuery, setSearchQuery] = React.useState("");
  
  // Garantir que options é sempre um array
  const safeOptions = Array.isArray(options) ? options : [];
  
  // Se houver busca assíncrona e resultados, usar os resultados
  const optionsToUse = onSearch && searchQuery ? searchResults : safeOptions;
  
  console.log('SearchSelect - optionsToUse:', optionsToUse.length, 'searchQuery:', searchQuery);
  
  // Converter os options para o formato esperado pela combobox
  const formattedOptions = optionsToUse.map(option => ({
    value: option._id || option.id || option,
    label: getOptionLabel ? getOptionLabel(option) : option.name || option.toString()
  }));
  
  console.log('SearchSelect - formattedOptions:', formattedOptions.length);

  // Estado para controlar o valor do Combobox
  const [inputValue, setInputValue] = React.useState("");
  
  // Callback para busca assíncrona
  const handleSearchChange = React.useCallback(async (query) => {
    if (!onSearch || !query) {
      setSearchResults([]);
      setSearchQuery("");
      return;
    }
    
    setIsSearching(true);
    setSearchQuery(query);
    try {
      const results = await onSearch(query);
      console.log('SearchSelect received results:', results.length, results);
      setSearchResults(Array.isArray(results) ? results : []);
    } catch (error) {
      console.error('Error searching:', error);
      setSearchResults([]);
    } finally {
      setIsSearching(false);
    }
  }, [onSearch]);
  
  // Para multi-select, mostrar as seleções atuais
  const currentSelections = multi && Array.isArray(value)
    ? value.map(item => {
        if (!item) return '';
        const itemValue = item._id || item.id || item;
        const option = optionsToUse.find(o => (o._id || o.id || o) === itemValue);
        return option ? (getOptionLabel ? getOptionLabel(option) : option.name || option.toString()) : itemValue;
      })
    : [];
    
  // Remover um item da seleção (apenas para multi-select)
  const removeItem = (indexToRemove) => {
    if (multi && value) {
      const newValue = [...value];
      newValue.splice(indexToRemove, 1);
      onChange(newValue);
    }
  };

  // For debugging
  React.useEffect(() => {
    console.log('SearchSelect value:', value);
    console.log('SearchSelect formatted options:', formattedOptions);
    if (!multi && value) {
      console.log('Current selected value:', value._id || value.id || value);
    }
  }, [value, formattedOptions, multi]);

  return (
    <div className="grid gap-2">
      {label && <Label htmlFor={label}>{label}</Label>}
      
      {/* Mostrar as seleções atuais para multi-select */}
      {multi && currentSelections.length > 0 && (
        <div className="flex flex-wrap gap-1 mb-2">
          {currentSelections.map((label, index) => (
            <div 
              key={index} 
              className="bg-primary/10 text-primary text-xs rounded-md px-2 py-1 flex items-center"
            >
              {label}
              <button 
                type="button"
                className="ml-1 hover:text-primary-dark"
                onClick={() => removeItem(index)}
              >
                ×
              </button>
            </div>
          ))}
        </div>
      )}
      
      <DirectCombobox
        options={formattedOptions}
        value={multi ? inputValue : value ? (value._id || value.id || value) : ""}
        onSearchChange={onSearch ? handleSearchChange : null}
        isSearching={isSearching}
        onChange={(val) => {
          console.log('DirectCombobox onChange called with:', val);
          
          if (!val || val === "") {
            if (!multi) {
              onChange(null);
            }
            setInputValue("");
            return;
          }

          if (multi) {
            const selectedOption = optionsToUse.find(o => (o._id || o.id || o) === val);
            console.log('Found multi option:', selectedOption);
            if (selectedOption && !value.some(v => (v._id || v.id || v) === val)) {
              onChange([...value, selectedOption]);
              setInputValue(""); // Reset input after selection
            }
          } else {
            const selectedOption = optionsToUse.find(o => (o._id || o.id || o) === val);
            console.log('Found single option:', selectedOption);
            if (selectedOption) {
              onChange(selectedOption);
            }
          }
        }}
        placeholder={t('negotiation.modal.selectPlaceholder', { item: label || t('negotiation.modal.defaultItem') })}
        className="w-full"
      />
    </div>
  );
};

export default function NegotiationModal({ negotiationId, onSave }) {
  const { t } = useTranslation();
  const [buyers, setBuyers] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [n, setN] = useState({
    stage: 'recebido',
    vehicles: [],
    responsible: [],
    comments: []
  });

  /* Função para buscar veículos por placa */
  const searchVehicles = useCallback(async (query) => {
    try {
      console.log('Searching vehicles with query:', query);
      const response = await api.get('/veiculos', {
        params: {
          placa: query,
          limit: 50 // buscar até 50 resultados
        }
      });
      console.log('Search response:', response.data);
      const vehicles = response.data.vehicles || response.data || [];
      console.log('Vehicles found:', vehicles.length, vehicles);
      return vehicles;
    } catch (err) {
      console.error('Error searching vehicles:', err);
      return [];
    }
  }, []);

  /* load estático */
  useEffect(() => {
    const fetchInitialData = async () => {
      try {
        setLoading(true);
        const [buyersResponse, usersResponse] = await Promise.all([
          api.get('/compradores'),
          api.get('/users')
        ]);
        console.log('Buyers loaded:', buyersResponse.data);
        console.log('Users loaded:', usersResponse.data);
        setBuyers(buyersResponse.data);
        setUsers(usersResponse.data);
        // Não carregar veículos inicialmente, apenas quando buscar
      } catch (err) {
        setError(err.message);
        console.error('Error fetching initial data:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchInitialData();
  }, []);

  /* load edição */
  useEffect(() => {
    if (!negotiationId) {
      setN(prev => ({ ...prev, _id: undefined }));
      return;
    }

    const fetchNegotiation = async () => {
      try {
        setLoading(true);
        console.log('Fetching negotiation with ID:', negotiationId, 'Type:', typeof negotiationId);
        const response = await api.get(`/negotiations/${negotiationId}`);
        setN(response.data);
      } catch (err) {
        setError(err.response?.data?.error || err.message);
        console.error('Error fetching negotiation:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchNegotiation();
  }, [negotiationId]);

  /* helpers */
  const total = n.vehicles.reduce((s, v) => s + (v.comercial?.valorVenda || 0), 0);
  
  /* comentários */
  const [cmt, setCmt] = useState('');
  
  const addComment = useCallback(async () => {
    if (!cmt.trim()) return;
    try {
      const response = await api.post(`/negotiations/${n._id}/comment`, { 
        user: 'me', 
        text: cmt 
      });
      setN(prev => ({ ...prev, comments: [...prev.comments, response.data] }));
      setCmt('');
    } catch (err) {
      setError(err.message);
      console.error('Error adding comment:', err);
    }
  }, [cmt, n._id]);

  if (error) {
    return (
      <div className="p-4 text-red-500">
        {t('negotiation.modal.error')}: {error}. {t('negotiation.modal.tryRefresh')}
      </div>
    );
  }

  if (loading) {
    return (
      <div className="p-4 text-center">
        <div className="animate-spin h-6 w-6 border-2 border-primary border-t-transparent rounded-full mx-auto"></div>
        <p className="mt-2 text-sm text-muted-foreground">{t('negotiation.modal.loading')}</p>
      </div>
    );
  }

  return (
    <>
      <DialogHeader>
        <DialogTitle>
          {n._id ? t('negotiation.modal.editTitle') : t('negotiation.modal.newTitle')}
        </DialogTitle>
      </DialogHeader>

      <ScrollArea className="max-h-[calc(80vh-10rem)]">
        <div className="p-4 space-y-6">
          {/* comprador */}
          <div className="space-y-4">
            <SearchSelect
              label={t('negotiation.modal.fields.buyer')}
              options={buyers}
              getOptionLabel={o => o.nomeFantasia}
              value={n.buyerId || null}
              onChange={v => setN(prev => ({ ...prev, buyerId: v }))}
            />

            {/* veículos */}
            <div className="space-y-2">
              <SearchSelect
                label={t('negotiation.modal.fields.vehicles')}
                options={n.vehicles || []}
                getOptionLabel={o => o.placa}
                value={n.vehicles}
                onChange={v => setN(prev => ({ ...prev, vehicles: v }))}
                onSearch={searchVehicles}
                multi
              />
              <div className="flex items-center justify-end text-sm mt-2">
                <span className="text-muted-foreground mr-2">
                  {t('negotiation.modal.totalValue')}:
                </span>
                <span className="text-green-600 font-semibold">
                  {new Intl.NumberFormat('pt-BR', {
                    style: 'currency',
                    currency: 'BRL'
                  }).format(total)}
                </span>
              </div>
            </div>

            {/* responsáveis */}
            <div>
              <SearchSelect
                label={t('negotiation.modal.fields.responsible')}
                options={users}
                getOptionLabel={o => `${o.nome} (${o.email})`}
                value={n.responsible}
                onChange={v => setN(prev => ({ ...prev, responsible: v }))}
                multi
              />
            </div>

            {/* comentários */}
            {n._id && (
              <div className="space-y-4 pt-2">
                <Separator />
                <h3 className="font-medium text-base">{t('negotiation.modal.comments.title')}</h3>

                <div className="space-y-3">
                  {n.comments.map((c, i) => (
                    <Card key={i} className="bg-muted/50">
                      <CardContent className="p-3">
                        <div className="flex items-center gap-2 text-xs text-muted-foreground mb-1">
                          <span className="font-semibold text-primary">{c.user}</span>
                          <span>{new Date(c.created).toLocaleString()}</span>
                        </div>
                        <p className="text-sm">{c.text}</p>
                      </CardContent>
                    </Card>
                  ))}
                </div>

                <div className="flex gap-2 mt-3">
                  <Textarea
                    value={cmt}
                    onChange={e => setCmt(e.target.value)}
                    placeholder={t('negotiation.modal.comments.placeholder')}
                    className="min-h-10 flex-1"
                  />
                  <Button 
                    variant="default" 
                    onClick={addComment}
                    disabled={loading || !cmt.trim()}
                    size="icon"
                  >
                    {loading ? (
                      <div className="animate-spin h-4 w-4 border-2 border-current border-t-transparent rounded-full" />
                    ) : (
                      <Send className="h-4 w-4" />
                    )}
                  </Button>
                </div>
              </div>
            )}
          </div>
        </div>
      </ScrollArea>

      <DialogFooter className="flex items-center border-t px-6 py-4">
        {n._id && (
          <Button
            variant="ghost"
            size="icon"
            className="text-red-500 hover:text-red-600 hover:bg-red-50 mr-auto"
            onClick={async () => {
              try {
                setLoading(true);
                await api.delete(`/negotiations/${n._id}`);
                onSave(true);
              } catch (err) {
                setError(err.message);
              } finally {
                setLoading(false);
              }
            }}
            disabled={loading}
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        )}

        {error && (
          <div className="flex-1 mx-4">
            <div className="p-2 bg-red-50 text-red-600 rounded-md text-sm">
              {error}
            </div>
          </div>
        )}

        <div className="flex gap-2">
          <Button 
            variant="outline"
            onClick={() => onSave(false)}
            disabled={loading}
          >
            {t('common.cancel')}
          </Button>
          <Button 
            variant="default"
            onClick={async () => {
              try {
                setLoading(true);
                setError(null);
                
                console.log('Saving negotiation data:', n);
                
                const payload = {
                  ...(n.buyerId && { buyerId: n.buyerId?._id || n.buyerId }),
                  vehicles: n.vehicles.map(v => v._id || v),
                  stage: n.stage,
                  responsible: n.responsible.map(u => u.email || u)
                };
                
                console.log('Payload to send:', payload);
                
                if (n._id) {
                  await api.put(`/negotiations/${n._id}`, payload);
                } else {
                  await api.post('/negotiations', payload);
                }
                onSave(true);
              } catch (err) {
                console.error('Error saving negotiation:', err);
                setError(err.response?.data?.error || err.message || t('negotiation.modal.saveError'));
              } finally {
                setLoading(false);
              }
            }}
            disabled={loading}
          >
            {loading ? (
              <>
                <div className="animate-spin mr-2 h-4 w-4 border-2 border-current border-t-transparent rounded-full"></div>
                {t('negotiation.modal.saving')}
              </>
            ) : t('common.save')}
          </Button>
        </div>
      </DialogFooter>
    </>
  );
}